import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Play, Square, RotateCcw, CheckCircle2, AlertTriangle, Smartphone, Crosshair } from "lucide-react";
import { toast } from "sonner";

/**
 * GirdDigitalGoniometer
 * ─────────────────────
 * Smartphone-based digital goniometer following a validated
 * protocol for measuring shoulder internal rotation in throwing
 * athletes (ICC 0.91–0.99 vs manual goniometry).
 *
 * Protocol implemented:
 *   1. Athlete SUPINE on the floor / table
 *   2. Shoulder abducted to 90° (scapular plane), elbow flexed to 90°
 *   3. Towel-bolster under the humerus to keep arm in the frontal plane
 *   4. Clinician/parent applies C-shaped stabilization: thumb on coracoid
 *      process anteriorly, fingers wrapped around the posterior scapula
 *   5. Phone strapped to the DISTAL FOREARM (over the wrist), screen up,
 *      long-axis aligned with the ulnar shaft pointing toward the ulnar
 *      styloid. Axis of measurement passes through the olecranon.
 *   6. ZERO the gauge with the forearm vertical (thumb-up, perpendicular
 *      to the floor) — this is the 0° starting baseline
 *   7. Rotate forearm INWARD/DOWNWARD toward the floor — the gauge records
 *      the peak angle traversed from vertical
 *   8. Stop at the FIRST FIRM ENDPOINT or when scapular hiking begins
 *
 * GIRD = Non-Dominant IR − Dominant IR  (positive = deficit on throwing arm).
 *
 * Sensor: DeviceOrientationEvent.beta  (front/back tilt, −180..180°)
 *   • Forearm vertical, screen up → beta ≈ 90°
 *   • Forearm horizontal toward floor → beta ≈ 0°
 *   • We capture the beta at "Start" as the zero baseline and report
 *     (zero − current) as the IR angle traversed.
 *
 * Props:
 *   • label      — e.g. "Dominant arm" / "Non-dominant arm"
 *   • measureType — "IR" or "ER" (affects only the help copy)
 *   • onDone     — onDone(angle_deg, method) called when user confirms
 *   • onCancel
 */
export default function GirdDigitalGoniometer({ label, measureType = "IR", onDone, onCancel }) {
  const [permission, setPermission] = useState("pending"); // pending | granted | denied | unsupported
  const [phase, setPhase] = useState("setup");             // setup | zeroing | recording | done | manual
  const [currentRawBeta, setCurrentRawBeta] = useState(null);
  const [zeroBeta, setZeroBeta] = useState(null);
  const [currentAngle, setCurrentAngle] = useState(0);
  const [peakAngle, setPeakAngle] = useState(0);
  const [manualValue, setManualValue] = useState("");
  const autoStopRef = useRef(null);
  const sensorWatchdogRef = useRef(null);
  const sawAnyOrientationEventRef = useRef(false);

  // ─── Sensor support detection ────────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined" || !window.DeviceOrientationEvent) {
      setPermission("unsupported");
      return;
    }
    if (typeof DeviceOrientationEvent.requestPermission !== "function") {
      setPermission("granted");  // Android / desktop Chrome
    }
  }, []);

  const requestSensor = async () => {
    if (typeof DeviceOrientationEvent.requestPermission === "function") {
      try {
        const r = await DeviceOrientationEvent.requestPermission();
        setPermission(r === "granted" ? "granted" : "denied");
        if (r !== "granted") toast.error("Motion sensor permission denied — switch to manual entry below.");
      } catch {
        setPermission("denied");
      }
    } else {
      setPermission("granted");
    }
  };

  // ─── Orientation listener (always on once granted, used by all phases) ─
  useEffect(() => {
    if (permission !== "granted") return;
    const handler = (e) => {
      const beta = typeof e.beta === "number" ? e.beta : null;
      // Only mark the sensor as "alive" if the event contains a real numeric
      // beta reading — Chromium emits a single null-payload event on listener
      // attach in non-mobile contexts, which would otherwise defuse the 4s
      // unsupported-watchdog below.
      if (beta !== null) sawAnyOrientationEventRef.current = true;
      setCurrentRawBeta(beta);
      if (phase === "recording" && zeroBeta !== null && beta !== null) {
        // IR angle = how far the forearm has rotated FROM the zero baseline
        // (vertical) toward the floor. zeroBeta ≈ 90 (vertical); beta drops
        // as the forearm rotates toward horizontal.
        const delta = Math.max(0, Math.min(180, Math.round(zeroBeta - beta)));
        setCurrentAngle(delta);
        setPeakAngle((p) => (delta > p ? delta : p));
      }
    };
    window.addEventListener("deviceorientation", handler, true);
    // Watchdog: in desktop Chrome (and headless test environments) the
    // DeviceOrientationEvent class exists but never fires. If no event
    // arrives within 4 seconds of granting permission, transparently flip
    // to manual-entry mode so the user isn't stuck.
    sensorWatchdogRef.current = setTimeout(() => {
      if (!sawAnyOrientationEventRef.current) {
        setPermission("unsupported");
      }
    }, 4000);
    return () => {
      window.removeEventListener("deviceorientation", handler, true);
      if (sensorWatchdogRef.current) clearTimeout(sensorWatchdogRef.current);
    };
  }, [permission, phase, zeroBeta]);

  // ─── Phase transitions ───────────────────────────────────────────────
  const beginZeroing = async () => {
    if (permission === "pending") await requestSensor();
    if (permission === "denied" || permission === "unsupported") {
      toast.error("Motion sensor unavailable — use manual entry below.");
      return;
    }
    setPhase("zeroing");
  };

  const captureZero = () => {
    if (currentRawBeta === null) {
      toast.error("Waiting for sensor reading — keep the phone still and try again.");
      return;
    }
    setZeroBeta(currentRawBeta);
    setCurrentAngle(0);
    setPeakAngle(0);
    setPhase("recording");
    // Safety auto-stop after 30s
    autoStopRef.current = setTimeout(() => {
      setPhase((p) => (p === "recording" ? "done" : p));
    }, 30000);
  };

  const stop = () => {
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    setPhase("done");
  };

  const reset = () => {
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    setPhase("setup");
    setZeroBeta(null);
    setPeakAngle(0);
    setCurrentAngle(0);
  };

  const useManual = () => {
    const v = parseFloat(manualValue);
    if (!Number.isFinite(v) || v < 0 || v > 180) {
      toast.error("Enter a value between 0 and 180 degrees.");
      return;
    }
    onDone(v, "manual");
  };

  // ─── Render ──────────────────────────────────────────────────────────
  const gaugeColor =
    phase === "done" ? "var(--brand-secondary)" :
    phase === "recording" ? "#22d3ee" :
    phase === "zeroing" ? "#facc15" : "#fff";

  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6" data-testid="gird-goniometer">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">{label}</div>
          <div className="text-lg font-display font-black text-white mt-1">
            {measureType === "IR" ? "Internal Rotation" : "External Rotation"} · ArmIQ Functional Movement protocol
          </div>
        </div>
        <Smartphone className="h-5 w-5 text-white/40" />
      </div>

      {/* Live angle gauge */}
      <div className="rounded-xl bg-black/40 border border-white/10 p-6 mb-4 text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">
          {phase === "setup"     && "Position the phone & stabilize the scapula"}
          {phase === "zeroing"   && "Hold forearm vertical, then capture zero"}
          {phase === "recording" && "Rotate forearm toward the floor — recording…"}
          {phase === "done"      && "Peak angle captured"}
        </div>
        <div className="font-display font-black text-7xl tabular-nums leading-none" data-testid="gird-angle-display"
             style={{ color: gaugeColor }}>
          {phase === "done" ? peakAngle : phase === "recording" ? currentAngle : 0}°
        </div>
        {phase === "recording" && (
          <div className="text-xs text-white/50 mt-2">
            Peak so far: <span className="font-bold text-white">{peakAngle}°</span>
          </div>
        )}
        {phase === "zeroing" && currentRawBeta !== null && (
          <div className="text-xs text-white/40 mt-2">Raw tilt: {Math.round(currentRawBeta)}° · capture when steady</div>
        )}
      </div>

      {/* Protocol checklist — only shown in setup phase */}
      {phase === "setup" && (
        <div className="rounded-lg border border-white/10 bg-black/20 p-4 mb-4 text-xs text-white/70 leading-relaxed">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">Before you begin</div>
          <ol className="space-y-1.5 list-decimal pl-4">
            <li>Athlete <span className="text-white font-bold">supine</span>, shoulder abducted <span className="text-white font-bold">90°</span>, elbow flexed <span className="text-white font-bold">90°</span>.</li>
            <li>Place a folded towel under the humerus so the arm stays in the frontal plane.</li>
            <li>Strap the phone to the <span className="text-white font-bold">distal forearm</span>, screen up, aligned with the ulnar shaft.</li>
            <li>Use <span className="text-white font-bold">C-shaped stabilization</span>: thumb on coracoid, fingers on posterior scapula.</li>
            <li>Forearm starts vertical (thumb-up) — that becomes 0°.</li>
            <li>Stop at the first firm endpoint <em>or</em> when the scapula starts to hike.</li>
          </ol>
        </div>
      )}

      {/* Controls */}
      {permission === "pending" && (
        <Button onClick={requestSensor} className="w-full" data-testid="gird-grant-permission">
          Enable Motion Sensor
        </Button>
      )}

      {permission === "granted" && phase === "setup" && (
        <Button onClick={beginZeroing} className="w-full" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="gird-start">
          <Crosshair className="h-4 w-4 mr-2" /> Continue — Zero the Gauge
        </Button>
      )}

      {phase === "zeroing" && (
        <div className="flex gap-2">
          <Button onClick={() => setPhase("setup")} variant="outline" className="flex-1 border-white/20 text-white" data-testid="gird-zero-back">
            Back
          </Button>
          <Button onClick={captureZero} className="flex-1" style={{ backgroundColor: "#facc15", color: "#0f172a" }} data-testid="gird-capture-zero">
            <Crosshair className="h-4 w-4 mr-2" /> Capture Zero
          </Button>
        </div>
      )}

      {phase === "recording" && (
        <Button onClick={stop} className="w-full" variant="destructive" data-testid="gird-stop">
          <Square className="h-4 w-4 mr-2" /> Stop & Record Peak {peakAngle}°
        </Button>
      )}

      {phase === "done" && (
        <div className="flex gap-2">
          <Button onClick={reset} variant="outline" className="flex-1 border-white/20 text-white" data-testid="gird-retake">
            <RotateCcw className="h-4 w-4 mr-2" /> Re-take
          </Button>
          <Button onClick={() => onDone(peakAngle, "self_gyro")} className="flex-1"
                  style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="gird-confirm">
            <CheckCircle2 className="h-4 w-4 mr-2" /> Save {peakAngle}°
          </Button>
        </div>
      )}

      {/* Manual entry — always reachable from the bottom of the card */}
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 mt-4" data-testid="gird-manual-fallback">
        <div className="flex items-center gap-2 text-amber-300 text-sm font-bold mb-2">
          <AlertTriangle className="h-4 w-4" />
          {(permission === "denied" || permission === "unsupported") ? "Manual entry" : "Don't have a phone strap?"}
        </div>
        <p className="text-xs text-white/60 mb-3">
          {(permission === "denied" || permission === "unsupported")
            ? "Sensor unavailable — enter the angle measured with a physical goniometer (axis on olecranon, moving arm along the ulnar shaft)."
            : "Enter the angle directly if you measured with a physical goniometer or another inclinometer app."}
        </p>
        <div className="flex gap-2">
          <input type="number" min="0" max="180" placeholder="degrees"
                 value={manualValue} onChange={(e) => setManualValue(e.target.value)}
                 className="flex-1 px-3 py-2 rounded bg-black/40 border border-white/20 text-white text-sm"
                 data-testid="gird-manual-input" />
          <Button onClick={useManual} data-testid="gird-manual-save">Save</Button>
        </div>
      </div>

      <button onClick={onCancel} className="mt-4 text-xs text-white/40 hover:text-white/70 w-full" data-testid="gird-cancel">
        Cancel this measurement
      </button>
    </Card>
  );
}

export function GirdLoadingPlaceholder() {
  return (
    <div className="flex items-center justify-center py-8 text-white/40 text-sm">
      <Loader2 className="h-4 w-4 animate-spin mr-2" /> Calibrating sensor…
    </div>
  );
}
